// One-time lobby announcement for smaller-match fallback.
// Bump SMALLER_MATCH_INTRO_VERSION if the copy needs to be shown again.

export const SMALLER_MATCH_INTRO_VERSION = 1;

export const SMALLER_MATCH_INTRO_TITLE =
  "Smaller matches when the queue is quiet";

export const SMALLER_MATCH_INTRO_LEAD =
  "If there aren't enough players for a 5v5, matchmaking can now offer a smaller game instead of making you wait.";

export const SMALLER_MATCH_INTRO_POINTS = [
  {
    title: "5v5 still comes first",
    body: "We only offer a smaller match when the queue stays too small to fill ten players.",
  },
  {
    title: "You choose",
    body: "You'll see 4v4, 3v3, or 2v2 while searching. Nothing starts unless you accept.",
  },
  {
    title: "Same skill matching",
    body: "Smaller games still use the same balancing rules. If a 5v5 becomes possible, that still wins.",
  },
];

export function getSmallerMatchOptedInCopy({
  teamLabel,
  optedInCount,
  matchSize,
  compact = false,
} = {}) {
  const optedIn = Number(optedInCount) || 0;
  const size = Number(matchSize) || 0;
  const ready = size > 0 && optedIn >= size;
  return {
    title: ready
      ? `Starting ${teamLabel}`
      : compact
        ? `Waiting for ${teamLabel}`
        : `Waiting for others to accept ${teamLabel}`,
    hint: ready
      ? `${optedIn}/${size} players opted in. Creating your match…`
      : `${optedIn}/${size} players opted in. 5v5 still wins if enough players join first.`,
  };
}

const STORAGE_KEY = "ouro.smallerMatchIntroAck.v1";

let memoryState = null;

function parseState(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function readFromStorage(storage) {
  if (!storage) return null;
  try {
    return parseState(storage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeToStorage(storage, raw) {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, raw);
  } catch {
    // Storage unavailable — memory fallback still holds the ack.
  }
}

function readState() {
  if (memoryState && typeof memoryState === "object") {
    return memoryState;
  }

  if (typeof window === "undefined") {
    return null;
  }

  const state =
    readFromStorage(window.localStorage) ||
    readFromStorage(window.sessionStorage);

  if (state) {
    memoryState = state;
  }
  return state;
}

function writeState(state) {
  memoryState = state;
  const raw = JSON.stringify(state);

  if (typeof window === "undefined") {
    return;
  }

  writeToStorage(window.localStorage, raw);
  writeToStorage(window.sessionStorage, raw);
}

function isAckedState(state) {
  if (!state || typeof state !== "object") return false;
  if (Number(state.version) !== SMALLER_MATCH_INTRO_VERSION) return false;
  return state.acked === true || state.acked === 1 || state.acked === "true";
}

export function shouldShowSmallerMatchIntro() {
  return !isAckedState(readState());
}

export function recordSmallerMatchIntroAck() {
  writeState({
    version: SMALLER_MATCH_INTRO_VERSION,
    acked: true,
    lastAckAt: Date.now(),
  });
}

export function resetSmallerMatchIntroAckForTests() {
  memoryState = null;
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    window.sessionStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
